/**
 * BookingCalendar - Calendar component for selecting available time slots.
 * Used in the public booking flow for scheduling meetings.
 */

import type { CalendarDate } from '@internationalized/date';
import {
  endOfMonth,
  endOfWeek,
  getLocalTimeZone,
  isSameDay,
  isSameMonth,
  isToday as isDateToday,
  parseDate,
  startOfMonth,
  startOfWeek,
  today,
} from '@internationalized/date';
import { useDateFormatter, useLocale } from '@react-aria/i18n';
import { ChevronLeft, ChevronRight } from '@untitledui/icons';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useRefreshAt } from '@/hooks/useRefreshAt';
import { getAvailability, getAvailabilityByCode } from '@/lib/api/scheduling';
import type {
  AvailabilityResponse,
  AvailableSlot,
} from '@/lib/api/types/scheduling';
import { Button } from '@/lib/base/buttons/button';
import { LoadingIndicator } from '@/lib/base/foundations/loading-indicator';
import { getTimezoneAbbreviation } from '@/lib/scheduling/timezone';
import { useTimeFormat } from '@/lib/scheduling/useTimeFormat';
import { cx } from '@/utils/cx';

// ============================================================================
// Types
// ============================================================================

export interface BookingCalendarProps {
  /** Meeting type identifier - can be username/slug or shortCode */
  meetingTypeId: string;
  /** Whether to use short code format */
  isShortCode?: boolean;
  /** Username for standard URL format */
  username?: string;
  /** Slug for standard URL format */
  slug?: string;
  /** Duration of the meeting in minutes */
  duration: number;
  /** Callback when a time slot is selected */
  onSlotSelect: (slot: AvailableSlot) => void;
  /** Timezone for display (IANA format) */
  timezone?: string;
  /**
   * Pre-fetched availability for the initial month range. When provided, the
   * calendar skips its first fetch and renders immediately with these slots.
   *
   * This is how BookingPage avoids the "skeleton → spinner → app" transition:
   * the page-level skeleton stays up until both the meeting type AND the
   * initial availability have resolved, then the page renders with the
   * calendar already populated. Subsequent month-nav fetches still go
   * through this component's own fetch flow (with the dim-and-disable
   * loading affordance rather than a centred spinner).
   */
  initialAvailability?: AvailabilityResponse;
  /**
   * Never fetch — render only what `initialAvailability` supplies.
   *
   * For surfaces with no saved booking link to fetch against, such as the
   * create-form preview, where `meetingTypeId` is a placeholder that would
   * 404. `initialAvailability`'s skip-the-first-fetch guard is not sufficient
   * on its own: it is a one-shot ref, so React StrictMode's double-invoked
   * mount effect consumes it on the first pass and fetches on the second, and
   * any later change to the visible month re-fetches regardless.
   */
  disableAvailabilityFetch?: boolean;
  /**
   * True when rendered inside the embed booking modal. The standalone page
   * keeps the tuned fixed slot-column height (matched to the calendar grid so
   * the card never reflows between days); the embed modal has more vertical
   * room, so the slot list is allowed to grow and show more times at once.
   */
  embed?: boolean;
}

interface TimeSlot extends AvailableSlot {
  displayTime: string;
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Returns true if the slot's start is in the past relative to `now`.
 * Used to dim slots that have already passed when the selected date is today.
 */
function isSlotInPast(isoString: string, now: Date): boolean {
  return new Date(isoString).getTime() <= now.getTime();
}

/**
 * Get the earliest future slot start time.
 * Used with `useRefreshAt` to re-evaluate which slots are past
 * as time progresses (e.g. when viewing today's slots).
 */
function getNextFutureSlotTime(
  slots: ReadonlyArray<{ start: string }>
): number | null {
  const now = Date.now();
  let earliest: number | null = null;
  for (const slot of slots) {
    const startMs = new Date(slot.start).getTime();
    if (startMs > now && (earliest === null || startMs < earliest)) {
      earliest = startMs;
    }
  }
  return earliest;
}

/**
 * Groups slots by date for easy lookup.
 */
function groupSlotsByDate(
  slots: AvailableSlot[],
  timezone: string
): Map<string, AvailableSlot[]> {
  const grouped = new Map<string, AvailableSlot[]>();

  slots.forEach(slot => {
    // Parse the ISO string and get the date in the target timezone
    const date = new Date(slot.start);
    const dateKey = date.toLocaleDateString('en-CA', { timeZone: timezone }); // YYYY-MM-DD format

    if (!grouped.has(dateKey)) {
      grouped.set(dateKey, []);
    }
    grouped.get(dateKey)?.push(slot);
  });

  return grouped;
}

/**
 * Checks if a CalendarDate has available slots.
 */
function hasAvailableSlots(
  date: CalendarDate,
  slotsByDate: Map<string, AvailableSlot[]>
): boolean {
  const dateKey = `${date.year}-${String(date.month).padStart(2, '0')}-${String(date.day).padStart(2, '0')}`;
  return (
    slotsByDate.has(dateKey) && (slotsByDate.get(dateKey)?.length ?? 0) > 0
  );
}

// ============================================================================
// Components
// ============================================================================

interface CalendarDayProps {
  date: CalendarDate;
  formattedDate: string;
  isCurrentMonth: boolean;
  isToday: boolean;
  isSelected: boolean;
  hasSlots: boolean;
  isDisabled: boolean;
  tabIndex: number;
  onClick: () => void;
  onKeyDown: (e: React.KeyboardEvent) => void;
}

function CalendarDay({
  date,
  formattedDate,
  isCurrentMonth,
  isToday,
  isSelected,
  hasSlots,
  isDisabled,
  tabIndex,
  onClick,
  onKeyDown,
}: CalendarDayProps) {
  return (
    <button
      type='button'
      onClick={onClick}
      onKeyDown={onKeyDown}
      disabled={isDisabled}
      tabIndex={tabIndex}
      role='gridcell'
      data-date={date.toString()}
      className={cx(
        // Rounded-square tile (Cal.com / Calendly convention). The square
        // reads as "a date you can pick"; circles read as badges/dots and
        // make selection state ambiguous on densely-numbered grids.
        // Smaller tile on mobile so the 7-column grid fits comfortably
        // in a 320px viewport (7×36 + 6×4 = 276px < 320 with margin).
        'relative flex h-9 w-9 items-center justify-center rounded-lg text-sm transition-colors sm:h-10 sm:w-10',
        // `focus-visible:` so the ring only appears for keyboard users.
        // `ring-inset` puts the ring INSIDE the tile so it doesn't add a
        // chunky outer outline that mimics selection on adjacent dates.
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--color-brand-600)]',
        // Base states
        isCurrentMonth ? 'text-fg-primary' : 'text-fg-disabled',
        // Available dates get a soft tinted background so they read as
        // clickable tiles. Past or no-slot dates stay flat.
        isCurrentMonth &&
          !isDisabled &&
          hasSlots &&
          !isSelected &&
          'cursor-pointer bg-[var(--color-bg-secondary)] font-medium hover:bg-[var(--color-bg-tertiary)]',
        // Other-month dates are clickable (jump to that month) when not past
        !isCurrentMonth &&
          !isDisabled &&
          'cursor-pointer hover:bg-bg-secondary',
        isDisabled && 'cursor-not-allowed opacity-50',
        // Today indicator
        isToday && !isSelected && 'font-semibold',
        // Selected state — solid brand fill, square tile.
        isSelected && 'bg-brand-solid text-white hover:bg-brand-solid_hover',
        // No slots available
        !hasSlots && !isDisabled && isCurrentMonth && 'text-fg-tertiary'
      )}
      aria-label={
        isCurrentMonth
          ? `${formattedDate}${hasSlots ? ', available' : ', no availability'}`
          : `${formattedDate}, jump to month`
      }
      aria-selected={isSelected}
      aria-disabled={isDisabled}
      aria-current={isToday ? 'date' : undefined}
    >
      {date.day}
      {/* Availability indicator dot */}
      {hasSlots && !isSelected && (
        <span
          aria-hidden='true'
          className={cx(
            'absolute bottom-1 left-1/2 h-1 w-1 -translate-x-1/2 rounded-full',
            isToday ? 'bg-brand-primary' : 'bg-fg-brand-primary'
          )}
        />
      )}
    </button>
  );
}

interface TimeSlotsListProps {
  slots: TimeSlot[];
  selectedSlot: AvailableSlot | null;
  onSlotSelect: (slot: AvailableSlot) => void;
  isLoading: boolean;
  selectedDateFormatted: string;
  /** Slots whose start time is in the past (when viewing today). */
  pastSlotKeys: ReadonlySet<string>;
  /** In the embed modal the list may grow to show more times (see BookingCalendarProps.embed). */
  embed?: boolean;
}

function TimeSlotsList({
  slots,
  selectedSlot,
  onSlotSelect,
  isLoading,
  selectedDateFormatted,
  embed = false,
  pastSlotKeys,
}: TimeSlotsListProps) {
  const listRef = useRef<HTMLDivElement>(null);

  // Focus the first BOOKABLE slot when the slot list changes (e.g. the
  // booker selected a new date). Earlier this targeted `slots[0]` directly,
  // but with past-slot disabling on the today view that's frequently the
  // first row in the list and `.focus()` on a `disabled` button is a no-op
  // — focus would silently fall back to <body>. Querying for the first
  // non-disabled option button lets us land focus on the slot the booker
  // can actually pick. If every slot is in the past we skip focusing
  // entirely (better than yanking focus to the listbox container).
  useEffect(() => {
    if (slots.length === 0) {
      return;
    }
    // Short delay to ensure DOM is ready (slot buttons may render in a
    // subsequent paint when the parent grid is rebuilding columns).
    const timer = setTimeout(() => {
      const firstEnabled = listRef.current?.querySelector<HTMLButtonElement>(
        'button[role="option"]:not([disabled])'
      );
      firstEnabled?.focus();
    }, 100);
    return () => clearTimeout(timer);
  }, [slots]);

  // Height strategy differs by surface, and all three render states
  // (loading / empty / populated) share it so a day switch never jumps:
  //   • Standalone page: a fixed 296px block (tuned to the desktop calendar
  //     grid height minus the slot column's date header) keeps the right
  //     column a constant height between days — the Cal.com / Calendly trick.
  //   • Embed modal: `flex-1` instead, so the slot area FILLS whatever height
  //     the calendar grid dictates (the row stretches both columns to equal
  //     height). The modal is therefore always calendar-height and never
  //     reserves a tall empty block on a sparse day the way a fixed 296px
  //     does — that reserved space is what made the popup look oversized with
  //     only a few slots. `min-h-0` lets the flex child shrink so overflow can
  //     scroll on busy days.
  if (isLoading) {
    return (
      <div
        className={cx(
          'flex items-center justify-center',
          embed ? 'min-h-0 flex-1' : 'h-[296px]'
        )}
        role='status'
      >
        <LoadingIndicator size='sm' label='Loading times…' />
      </div>
    );
  }

  if (slots.length === 0) {
    return (
      <div
        className={cx(
          'flex items-center justify-center text-center text-fg-secondary',
          embed ? 'min-h-0 flex-1' : 'h-[296px]'
        )}
        role='status'
      >
        No available times for this date
      </div>
    );
  }

  return (
    <div
      ref={listRef}
      className={cx(
        'gap-2 overflow-y-auto pr-1',
        // Standalone: a single scannable column (Cal.com convention) at a fixed
        // 296px height, so the column stays constant regardless of slot count —
        // overflow scrolls busy days, empty space sits below sparse ones, and
        // the page never reflows mid-interaction.
        //
        // Embed modal: a 2-column grid that FILLS the calendar-driven height
        // (`flex-1`) instead of forcing 296px. Two columns show ~2x as many
        // times at once, and filling the calendar height means a sparse day
        // hugs the calendar instead of reserving a tall empty block — so the
        // popup stays compact rather than looking oversized. `content-start`
        // packs rows to the top; `min-h-0` lets the grid shrink so it scrolls.
        embed
          ? 'grid min-h-0 flex-1 grid-cols-2 content-start'
          : 'flex h-[296px] flex-col'
      )}
      role='listbox'
      aria-label={`Available times for ${selectedDateFormatted}`}
    >
      {slots.map(slot => {
        const isSelected =
          selectedSlot?.start === slot.start && selectedSlot?.end === slot.end;
        const isPast = pastSlotKeys.has(slot.start);

        return (
          <button
            key={slot.start}
            type='button'
            role='option'
            onClick={() => onSlotSelect(slot)}
            disabled={isPast}
            className={cx(
              // px-4 + py-2.5 = 42px total height (text-sm 20px line +
              // 20px vertical padding + 2px border). Previously `py-3`
              // gave 46px which read as visually heavy and meant fewer
              // slots fit before the list scrolled. 42px stays tappable
              // on touch targets (≥40px Apple HIG recommendation) while
              // letting 6-7 slots fit in the now-fixed 296px slot column.
              'w-full rounded-lg border px-4 py-2.5 text-sm font-medium transition-all',
              // `focus-visible:` so a mouse click doesn't flash a heavy ring
              // that reads as a selection. `ring-inset` keeps the ring INSIDE
              // the pill border instead of sitting outside it with a gap —
              // outside-with-offset reads as a chunky outer outline that
              // mimics selection. With inset, the ring just deepens the
              // existing border for keyboard users without changing the
              // button's footprint.
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--color-brand-600)]',
              isPast &&
                'cursor-not-allowed border-[var(--color-border-secondary)] bg-[var(--color-bg-primary)] text-[var(--color-text-tertiary)] opacity-50',
              !isPast &&
                (isSelected
                  ? 'border-[var(--color-brand-600)] bg-[var(--color-brand-50)] text-[var(--color-brand-700)]'
                  : 'border-[var(--color-border-secondary)] bg-[var(--color-bg-primary)] text-[var(--color-text-primary)] hover:border-[var(--color-brand-600)] hover:bg-[var(--color-bg-secondary)]')
            )}
            aria-selected={isSelected}
            aria-label={`${slot.displayTime}${isPast ? ', past' : ''}${isSelected ? ', selected' : ''}`}
          >
            {slot.displayTime}
          </button>
        );
      })}
    </div>
  );
}

interface TimeFormatToggleProps {
  format: '12h' | '24h';
  onChange: (next: '12h' | '24h') => void;
}

function TimeFormatToggle({ format, onChange }: TimeFormatToggleProps) {
  // Inline segmented control. Two states only — keeping it inline avoids
  // pulling in a new primitive for a single use site. If we ever add a third
  // option, extract this into lib/base/segmented-control.
  const handle12h = useCallback(() => onChange('12h'), [onChange]);
  const handle24h = useCallback(() => onChange('24h'), [onChange]);
  return (
    <div
      role='radiogroup'
      aria-label='Time format'
      className='inline-flex items-center rounded-md border border-[var(--color-border-secondary)] bg-[var(--color-bg-primary)] p-0.5 text-xs'
    >
      <button
        type='button'
        role='radio'
        aria-checked={format === '12h'}
        onClick={handle12h}
        className={cx(
          'rounded px-2 py-1 font-medium transition-colors',
          format === '12h'
            ? 'bg-[var(--color-bg-tertiary)] text-[var(--color-text-primary)]'
            : 'text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]'
        )}
      >
        12h
      </button>
      <button
        type='button'
        role='radio'
        aria-checked={format === '24h'}
        onClick={handle24h}
        className={cx(
          'rounded px-2 py-1 font-medium transition-colors',
          format === '24h'
            ? 'bg-[var(--color-bg-tertiary)] text-[var(--color-text-primary)]'
            : 'text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]'
        )}
      >
        24h
      </button>
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

export function BookingCalendar({
  meetingTypeId,
  isShortCode = false,
  username,
  slug,
  duration,
  onSlotSelect,
  timezone: propTimezone,
  initialAvailability,
  disableAvailabilityFetch = false,
  embed = false,
}: BookingCalendarProps) {
  const { locale } = useLocale();
  const browserTimezone = useMemo(() => getLocalTimeZone(), []);
  const timezone = propTimezone || browserTimezone;
  const {
    format: timeFormat,
    setFormat: setTimeFormat,
    formatTime,
  } = useTimeFormat();

  // State
  const [currentMonth, setCurrentMonth] = useState(() => today(timezone));
  const [selectedDate, setSelectedDate] = useState<CalendarDate | null>(null);
  const [focusedDate, setFocusedDate] = useState<CalendarDate | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<AvailableSlot | null>(null);
  // Pre-seed `availability` from `initialAvailability` when the parent has
  // already fetched it. This is how BookingPage avoids the brief spinner
  // between the page-level skeleton and the populated calendar — the parent
  // holds its skeleton until both the meeting type AND this month's slots
  // have resolved, then mounts us with `availability` already non-null.
  const [availability, setAvailability] = useState<AvailabilityResponse | null>(
    initialAvailability ?? null
  );
  // When we have pre-fetched slots there's nothing to load on mount; start
  // out non-loading so the spinner branch (`isLoading && !availability`)
  // doesn't fire on first paint.
  const [isLoading, setIsLoading] = useState(initialAvailability == null);
  const [error, setError] = useState<string | null>(null);
  const [announcement, setAnnouncement] = useState<string>('');

  // Refs for grid navigation
  const calendarGridRef = useRef<HTMLDivElement>(null);

  // Tracks whether the user has interacted with the calendar yet. Used to
  // gate the auto-select-earliest-date behavior to first-load only — after
  // any explicit click (date select, month nav), we never auto-override the
  // user's choice (or empty selection).
  const hasUserInteractedRef = useRef(false);

  // When the parent supplies `initialAvailability`, skip the very first
  // fetch — we already have the data. Subsequent fetches (month nav, TZ
  // change) still go through normally. The ref flips after the first
  // suppressed run so any later prop change re-arms the effect correctly.
  const skipNextFetchRef = useRef(initialAvailability != null);

  // Formatters — every one MUST pass `timeZone: timezone` so the formatted
  // string is in the same TZ as the CalendarDate we hand it.
  //
  // The trap we keep falling into: `CalendarDate.toDate(timezone)` returns a
  // JS Date pinned to midnight of that day in the booking TZ. When
  // `Intl.DateTimeFormat` formats that Date *without* a timeZone option, it
  // uses the BOOKER'S BROWSER TZ. For a booker viewing slots in Europe/Kiev
  // (UTC+3) from a browser in UTC, the Date corresponding to
  // `CalendarDate(2026-05-15, Kiev)` is actually `2026-05-14T21:00:00Z`, so
  // the formatter prints "Thursday, 14 May" — off-by-one from the selected
  // cell. The fix is universal: every formatter pinned to a CalendarDate
  // must format in that same TZ.
  const monthYearFormatter = useDateFormatter({
    month: 'long',
    year: 'numeric',
    timeZone: timezone,
  });
  const weekdayFormatter = useDateFormatter({
    weekday: 'short',
    timeZone: timezone,
  });
  const fullDateFormatter = useDateFormatter({
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    timeZone: timezone,
  });
  const selectedDateFormatter = useDateFormatter({
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    timeZone: timezone,
  });

  // Calculate calendar grid dates
  const calendarDays = useMemo(() => {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(currentMonth);
    const gridStart = startOfWeek(monthStart, locale);
    const gridEnd = endOfWeek(monthEnd, locale);

    const days: CalendarDate[] = [];
    let day = gridStart;
    while (day.compare(gridEnd) <= 0) {
      days.push(day);
      day = day.add({ days: 1 });
    }
    return days;
  }, [currentMonth, locale]);

  // Generate weekday headers
  const weekdays = useMemo(() => {
    const firstDay = startOfWeek(today(timezone), locale);
    return Array.from({ length: 7 }).map((_, i) =>
      weekdayFormatter.format(firstDay.add({ days: i }).toDate(timezone))
    );
  }, [locale, timezone, weekdayFormatter]);

  // Group slots by date
  const slotsByDate = useMemo(() => {
    if (!availability?.slots) {
      return new Map<string, AvailableSlot[]>();
    }
    return groupSlotsByDate(availability.slots, timezone);
  }, [availability, timezone]);

  // Get slots for selected date with display times
  const slotsForSelectedDate = useMemo((): TimeSlot[] => {
    if (!selectedDate) {
      return [];
    }

    const dateKey = `${selectedDate.year}-${String(selectedDate.month).padStart(2, '0')}-${String(selectedDate.day).padStart(2, '0')}`;
    const slots = slotsByDate.get(dateKey) || [];

    return slots.map(slot => ({
      ...slot,
      // Drop per-slot timezone label — the host card surfaces TZ once at the
      // top of the page so we don't repeat "GMT+1" on every button.
      displayTime: formatTime(slot.start, timezone),
    }));
  }, [selectedDate, slotsByDate, timezone, formatTime]);

  // Slots whose start is in the past. Used to dim/disable past times when
  // the selected date is today. Not memoized: the filter is cheap (~20 slots)
  // and must reflect the current time when useRefreshAt triggers a re-render.
  const pastSlotKeys = (() => {
    const set = new Set<string>();
    const now = new Date();
    for (const slot of slotsForSelectedDate) {
      if (isSlotInPast(slot.start, now)) {
        set.add(slot.start);
      }
    }
    return set;
  })();

  // Re-render when the next slot's start time passes so the
  // pastSlotKeys set updates and the slot gets dimmed/disabled.
  useRefreshAt(getNextFutureSlotTime(slotsForSelectedDate));

  // Fetch availability for the current month view
  const fetchAvailability = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Calculate date range: current month + buffer for visibility
      const startDate = startOfMonth(currentMonth);
      const endDate = endOfMonth(currentMonth).add({ months: 1 });

      const startDateStr = `${startDate.year}-${String(startDate.month).padStart(2, '0')}-${String(startDate.day).padStart(2, '0')}`;
      const endDateStr = `${endDate.year}-${String(endDate.month).padStart(2, '0')}-${String(endDate.day).padStart(2, '0')}`;

      let result: AvailabilityResponse;

      if (isShortCode) {
        result = await getAvailabilityByCode(meetingTypeId, {
          start_date: startDateStr,
          end_date: endDateStr,
          timezone,
        });
      } else if (username && slug) {
        result = await getAvailability(username, slug, {
          start_date: startDateStr,
          end_date: endDateStr,
          timezone,
        });
      } else {
        throw new Error('Invalid meeting type configuration');
      }

      setAvailability(result);
    } catch (caughtError) {
      setError(
        "We couldn't load availability. Check your connection and try again."
      );
      console.error('Failed to fetch availability:', caughtError);
    } finally {
      setIsLoading(false);
    }
  }, [currentMonth, isShortCode, meetingTypeId, username, slug, timezone]);

  // Fetch availability when month changes
  useEffect(() => {
    // Hard opt-out: this caller has no fetchable booking link, so never issue
    // a request. Checked before the one-shot ref below because that guard is
    // consumed by StrictMode's double-invoked mount effect.
    if (disableAvailabilityFetch) {
      return;
    }
    // Skip the very first run when the parent has already provided
    // `initialAvailability` — re-fetching the same window on mount would
    // throw away the pre-seeded data, flip `isLoading` back to true, and
    // briefly re-expose the spinner branch this prop was added to avoid.
    if (skipNextFetchRef.current) {
      skipNextFetchRef.current = false;
      return;
    }
    fetchAvailability();
  }, [fetchAvailability, disableAvailabilityFetch]);

  // Auto-select the earliest available date on first load. The fetch returns
  // current month + next month of slots, so we can jump the calendar forward
  // when there's nothing available this month — Calendly / Cal.com convention.
  // Gated by `hasUserInteractedRef` so we never override an explicit user
  // choice (or the user's deliberate "no selection" after navigating months).
  useEffect(() => {
    if (hasUserInteractedRef.current) {
      return;
    }
    if (!availability?.slots || availability.slots.length === 0) {
      return;
    }
    // The slots come back already sorted by start time, but we don't rely on
    // that — find the earliest by comparing ISO timestamps.
    let earliestSlot = availability.slots[0];
    for (const slot of availability.slots) {
      if (slot.start < earliestSlot.start) {
        earliestSlot = slot;
      }
    }
    const earliestDate = parseDate(
      new Date(earliestSlot.start).toLocaleDateString('en-CA', {
        timeZone: timezone,
      })
    );

    // If the earliest date is outside the visible month, jump the calendar
    // there first. The fetch effect won't refire because the next month of
    // slots is already in `availability` from the current fetch window.
    if (!isSameMonth(earliestDate, currentMonth)) {
      setCurrentMonth(startOfMonth(earliestDate));
    }
    setSelectedDate(earliestDate);
    setFocusedDate(earliestDate);
    // Deliberately don't set hasUserInteractedRef here — this is a default,
    // not an interaction. The flag flips only when the user explicitly
    // clicks a date or navigates months, which is the right boundary for
    // "respect their choice from now on".
  }, [availability, currentMonth, timezone]);

  // Handle date selection
  const handleDateSelect = useCallback(
    (date: CalendarDate) => {
      hasUserInteractedRef.current = true;
      setSelectedDate(date);
      setFocusedDate(date);
      setSelectedSlot(null);
      // Announce selection to screen readers
      const dateStr = fullDateFormatter.format(date.toDate(timezone));
      const hasSlots = hasAvailableSlots(date, slotsByDate);
      setAnnouncement(
        `${dateStr} selected. ${hasSlots ? 'Time slots available.' : 'No availability.'}`
      );
    },
    [fullDateFormatter, timezone, slotsByDate]
  );

  // Keyboard navigation for calendar grid
  const handleCalendarKeyDown = useCallback(
    (e: React.KeyboardEvent, currentDate: CalendarDate) => {
      let newDate: CalendarDate | null = null;
      const todayD = today(timezone);

      switch (e.key) {
        case 'ArrowLeft':
          newDate = currentDate.add({ days: -1 });
          break;
        case 'ArrowRight':
          newDate = currentDate.add({ days: 1 });
          break;
        case 'ArrowUp':
          newDate = currentDate.add({ days: -7 });
          break;
        case 'ArrowDown':
          newDate = currentDate.add({ days: 7 });
          break;
        case 'Home':
          newDate = startOfWeek(currentDate, locale);
          break;
        case 'End':
          newDate = endOfWeek(currentDate, locale);
          break;
        case 'PageUp':
          if (e.shiftKey) {
            newDate = currentDate.add({ years: -1 });
          } else {
            newDate = currentDate.add({ months: -1 });
          }
          break;
        case 'PageDown':
          if (e.shiftKey) {
            newDate = currentDate.add({ years: 1 });
          } else {
            newDate = currentDate.add({ months: 1 });
          }
          break;
        case 'Enter':
        case ' ': {
          const isCurrentMonthDate = isSameMonth(currentDate, currentMonth);
          const isPast = currentDate.compare(todayD) < 0;
          if (isPast) {
            e.preventDefault();
            return;
          }
          if (!isCurrentMonthDate) {
            // Mirror click behaviour: jump to the other month and select
            hasUserInteractedRef.current = true;
            setCurrentMonth(startOfMonth(currentDate));
            setSelectedDate(currentDate);
            setFocusedDate(currentDate);
            setSelectedSlot(null);
            e.preventDefault();
            return;
          }
          if (hasAvailableSlots(currentDate, slotsByDate)) {
            handleDateSelect(currentDate);
          }
          e.preventDefault();
          return;
        }
        default:
          return;
      }

      e.preventDefault();

      if (newDate) {
        // Prevent navigating to past dates
        if (newDate.compare(todayD) < 0) {
          newDate = todayD;
        }

        // Mark as user-interacted so the auto-select-earliest-date effect
        // doesn't override keyboard-driven navigation when availability
        // refetches (e.g. PageDown crossing into a new month triggers a fetch).
        hasUserInteractedRef.current = true;

        // Update month if navigating to a different month
        if (!isSameMonth(newDate, currentMonth)) {
          setCurrentMonth(startOfMonth(newDate));
        }

        setFocusedDate(newDate);

        // Focus the new date button
        const dateToFocus = newDate;
        requestAnimationFrame(() => {
          const dateStr = dateToFocus.toString();
          const button = calendarGridRef.current?.querySelector(
            `[data-date="${dateStr}"]`
          ) as HTMLButtonElement | null;
          button?.focus();
        });
      }
    },
    [timezone, locale, currentMonth, slotsByDate, handleDateSelect]
  );

  // Handle slot selection
  const handleSlotSelect = useCallback(
    (slot: AvailableSlot) => {
      setSelectedSlot(slot);
      onSlotSelect(slot);
    },
    [onSlotSelect]
  );

  // Navigation handlers
  const handlePreviousMonth = useCallback(() => {
    hasUserInteractedRef.current = true;
    setCurrentMonth(prev => prev.add({ months: -1 }));
    setSelectedDate(null);
    setFocusedDate(null);
    setSelectedSlot(null);
  }, []);

  const handleNextMonth = useCallback(() => {
    hasUserInteractedRef.current = true;
    setCurrentMonth(prev => prev.add({ months: 1 }));
    setSelectedDate(null);
    setFocusedDate(null);
    setSelectedSlot(null);
  }, []);

  const todayDate = today(timezone);
  const canGoPrevious = currentMonth.compare(startOfMonth(todayDate)) > 0;

  return (
    <div className='flex flex-col gap-6 lg:flex-row lg:gap-8'>
      {/* Calendar Section */}
      <div className='flex-1'>
        {/* Calendar Header — month bold, year deemphasised (Cal.com pattern).
            Reinforces "you're picking a date in this month", not "scanning
            year+month". */}
        <div className='mb-4 flex items-center justify-between'>
          <h2 className='text-lg font-semibold text-fg-primary'>
            {currentMonth.toDate(timezone).toLocaleDateString(locale, {
              month: 'long',
              // Same off-by-one guard as the useDateFormatter calls above
              // — at month boundaries (`CalendarDate(2026-05-01)` →
              // 2026-04-30 21:00 UTC in Kiev TZ), the month name would
              // shift backwards when formatted in a westward browser TZ.
              timeZone: timezone,
            })}{' '}
            <span className='font-normal text-[var(--color-text-tertiary)]'>
              {currentMonth.year}
            </span>
          </h2>
          <div className='flex gap-1'>
            <Button
              size='sm'
              color='tertiary'
              onClick={handlePreviousMonth}
              isDisabled={!canGoPrevious}
              aria-label='Previous month'
              iconLeading={ChevronLeft}
            />
            <Button
              size='sm'
              color='tertiary'
              onClick={handleNextMonth}
              aria-label='Next month'
              iconLeading={ChevronRight}
            />
          </div>
        </div>

        {/* Loading State */}
        {isLoading && !availability && (
          <div className='flex items-center justify-center py-16'>
            <LoadingIndicator size='md' label='Loading availability…' />
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className='rounded-lg border border-error-200 bg-error-50 p-4 text-center text-error-600'>
            {error}
            <button
              type='button'
              onClick={fetchAvailability}
              className='ml-2 text-error-700 underline hover:text-error-800 hover:no-underline'
            >
              Retry
            </button>
          </div>
        )}

        {/* Calendar Grid */}
        {!error && availability && (
          <div
            role='grid'
            aria-label={`Calendar for ${monthYearFormatter.format(currentMonth.toDate(timezone))}`}
            ref={calendarGridRef}
            aria-busy={isLoading}
            className={cx(
              'transition-opacity duration-150',
              // While re-fetching availability for a new month, dim the grid
              // so users see fetch is in progress — but keep it in place to
              // avoid the layout reflow you'd get from swapping it for a
              // skeleton/spinner.
              isLoading && 'pointer-events-none opacity-50'
            )}
          >
            {/* Weekday Headers */}
            <div className='mb-2 grid grid-cols-7 gap-1' role='row'>
              {weekdays.map(day => (
                <div
                  key={day}
                  role='columnheader'
                  aria-label={day}
                  className='flex h-10 items-center justify-center text-sm font-medium text-fg-secondary'
                >
                  {day.slice(0, 2)}
                </div>
              ))}
            </div>

            {/* Days Grid */}
            <div className='grid grid-cols-7 gap-1' role='rowgroup'>
              {calendarDays.map((date, index) => {
                const isCurrentMonth = isSameMonth(date, currentMonth);
                const isToday = isDateToday(date, timezone);
                const isSelected = selectedDate
                  ? isSameDay(date, selectedDate)
                  : false;
                const isFocused = focusedDate
                  ? isSameDay(date, focusedDate)
                  : false;
                const hasSlots = hasAvailableSlots(date, slotsByDate);
                const isPast = date.compare(todayDate) < 0;
                // Past dates are always unbookable. Other-month dates are NOT
                // disabled — clicking them jumps the calendar to that month
                // instead (Calendly/Cal.com convention). Visually still greyed
                // out via the isCurrentMonth flag in CalendarDay's classes.
                const isDisabled = isPast;
                const formattedDate = fullDateFormatter.format(
                  date.toDate(timezone)
                );

                // Determine tab index: only one date should be tabbable
                // Priority: selected > focused > today > first available
                const isFirstDay = index === 0;
                let tabIndex = -1;
                if (isSelected) {
                  tabIndex = 0;
                } else if (!selectedDate && isFocused) {
                  tabIndex = 0;
                } else if (
                  !selectedDate &&
                  !focusedDate &&
                  isToday &&
                  isCurrentMonth
                ) {
                  tabIndex = 0;
                } else if (!selectedDate && !focusedDate && isFirstDay) {
                  tabIndex = 0;
                }

                return (
                  <CalendarDay
                    key={date.toString()}
                    date={date}
                    formattedDate={formattedDate}
                    isCurrentMonth={isCurrentMonth}
                    isToday={isToday}
                    isSelected={isSelected}
                    hasSlots={hasSlots}
                    isDisabled={isDisabled}
                    tabIndex={tabIndex}
                    onClick={() => {
                      if (isDisabled) {
                        return;
                      }
                      if (!isCurrentMonth) {
                        // Jump to the clicked date's month AND select the
                        // date so available times for it render in the right
                        // column. Trailing-row clicks (next month) already
                        // have slots loaded — the current fetch covers
                        // currentMonth + 1 month. Leading-row clicks (prev
                        // month) won't have slots until the fetch effect
                        // re-runs after `setCurrentMonth`; the right column
                        // briefly shows "no times" then populates once the
                        // new fetch resolves.
                        hasUserInteractedRef.current = true;
                        setCurrentMonth(startOfMonth(date));
                        setSelectedDate(date);
                        setFocusedDate(date);
                        setSelectedSlot(null);
                        return;
                      }
                      if (hasSlots) {
                        handleDateSelect(date);
                      }
                    }}
                    onKeyDown={e => handleCalendarKeyDown(e, date)}
                  />
                );
              })}
            </div>
          </div>
        )}

        {/* Timezone display intentionally removed here — surfaced once in
            BookingHostCard so the booking page doesn't repeat the same TZ
            label across three regions. */}
      </div>

      {/* Right column — always rendered to reserve layout space.
          Without this wrapper the left calendar (flex-1) would expand to fill
          the row whenever selectedDate clears (e.g. during month navigation
          while availability is being fetched), causing a visible reflow. */}
      <div className='flex flex-1 flex-col lg:max-w-xs'>
        {selectedDate ? (
          <>
            {/* Slot date header + 12h/24h toggle. On mobile (< sm) the
                header and toggle stack vertically — date + TZ caption can
                eat most of a 320px viewport, leaving no room for the
                segmented toggle beside them. At sm+ they sit side-by-side. */}
            <div className='mb-4 flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between sm:gap-3'>
              <div className='flex min-w-0 flex-col'>
                <h3 className='text-lg font-semibold text-fg-primary'>
                  {(() => {
                    // Show "Today" / "Tomorrow" relative labels when the
                    // selected date is near the current date — matches Cal.com
                    // and feels more conversational than the formal weekday.
                    if (isSameDay(selectedDate, todayDate)) {
                      return `Today, ${selectedDateFormatter.format(selectedDate.toDate(timezone))}`;
                    }
                    if (isSameDay(selectedDate, todayDate.add({ days: 1 }))) {
                      return `Tomorrow, ${selectedDateFormatter.format(selectedDate.toDate(timezone))}`;
                    }
                    return selectedDateFormatter.format(
                      selectedDate.toDate(timezone)
                    );
                  })()}
                </h3>
                {/* Compact TZ offset (e.g. "GMT+1") next to the slot date
                    header. Cal.com keeps the TZ in the host card; we show it
                    again here so when a booker switches TZ via the picker,
                    the numbers next to it visibly anchor to the new offset.
                    Per-pill annotation would be too noisy with 15–20 slots. */}
                <span className='mt-0.5 text-xs text-[var(--color-text-tertiary)]'>
                  All times in{' '}
                  {getTimezoneAbbreviation(
                    timezone,
                    selectedDate.toDate(timezone)
                  )}
                </span>
              </div>
              <div className='self-end sm:self-auto'>
                <TimeFormatToggle
                  format={timeFormat}
                  onChange={setTimeFormat}
                />
              </div>
            </div>

            <TimeSlotsList
              slots={slotsForSelectedDate}
              selectedSlot={selectedSlot}
              onSlotSelect={handleSlotSelect}
              isLoading={isLoading}
              selectedDateFormatted={selectedDateFormatter.format(
                selectedDate.toDate(timezone)
              )}
              pastSlotKeys={pastSlotKeys}
              embed={embed}
            />

            {selectedSlot && (
              <div className='mt-4 rounded-lg bg-bg-secondary p-4'>
                <p className='text-sm text-fg-secondary'>
                  Selected: {formatTime(selectedSlot.start, timezone)} (
                  {duration} min)
                </p>
              </div>
            )}
          </>
        ) : (
          <div className='flex flex-1 items-center justify-center text-fg-secondary'>
            <p>
              {isLoading
                ? 'Loading available times…'
                : 'Select a date to view available times'}
            </p>
          </div>
        )}
      </div>

      {/* Live region for screen reader announcements */}
      <div
        role='status'
        aria-live='polite'
        aria-atomic='true'
        className='sr-only'
      >
        {announcement}
      </div>
    </div>
  );
}
